import { useRef } from 'react';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import awardedFeatureSVG from '../../assets/frb-widget/awardedFeature.svg';
import coinsPile from '../../assets/frb-widget/coinsPile.png';
import offerSVG from '../../assets/frb-widget/offerHome.svg';
import rubySVG from '../../assets/frb-widget/rubyFRB.svg';
import rubyHomeSVG from '../../assets/frb-widget/rubyFRBHome.svg';
import { useFlag } from '../../services/launchDarkly';
import { formatValueWithK } from '../../utils/helpers';
import { useVwToPx } from '../EventTriggerDialog/hooks/useVwToPx';
import { SocialCoin } from '../SocialCoin/SocialCoin';
import { SweepCoin } from '../SweepCoin/SweepCoin';

import BaroulettaPopupImage from './BaroulettaPopupImage';
import { CountdownType } from './components/CountdownType';
import { FreeSpinsType } from './components/FreeSpinsType';
import { colorsMapping } from './constants';
import classes from './FRBWidget.module.scss';
import { FreeSpinsIconNew } from './FreeSpinsIconNew/FreeSpinsIconNew';
import { isCountdownType, isFreeSpinsType, type FRBWidgetProps } from './types';

export function FRBWidget(props: FRBWidgetProps) {
  const newFreeSpinsIconUiFlag = useFlag<boolean>('new_free_spins_icon_ui', false);
  const { t } = useTranslation();
  const {
    coinType: betCoinType,
    betAmount,
    gameImage,
    width,
    provider,
    height,
    dataTestId,
    maxPrize,
    rounds,
    displayCount,
    freeSpinType,
    fontSize: propFontSize,
    usePropFontSize = false,
  } = props;
  const parentRef = useRef(null);
  const calculatedFontSize = useVwToPx({ ref: parentRef, vwValue: 10, width });
  const fontSize = usePropFontSize ? propFontSize : calculatedFontSize;

  const { color, giftIcon } = colorsMapping[props.type];
  const CoinIcon = betCoinType === 'GC' ? SocialCoin : SweepCoin;

  if (['Barouletta Gaming', 'Minigames'].includes(provider || '')) {
    return (
      <div
        ref={parentRef}
        className={classNames('circle', props.className)}
        data-testid={dataTestId}
        style={{ width, height }}
      >
        <BaroulettaPopupImage
          amount={maxPrize ? (maxPrize?.type === 'coins' ? maxPrize.amount : rounds) : undefined}
          coinType={maxPrize?.type === 'coins' ? maxPrize.coinType : undefined}
          displayCount={displayCount}
          imageUrl={gameImage}
          maxPrize={maxPrize}
          type={maxPrize?.type}
        />
      </div>
    );
  }

  if (freeSpinType === 'AWARDED_FEATURE') {
    return (
      <div
        ref={parentRef}
        className={classNames(classes.frbWidgetAwarded, props.className)}
        data-testid={dataTestId}
        style={{ fontSize, width, height }}
      >
        {/* Game image section */}
        <div className={classes.gameImageContainer}>
          <img alt="Game" className={classes.gameImage} src={gameImage} />
        </div>

        {/* Offer section for home widget */}
        <div className={classes.providerSectionHome}>
          <img alt="Ruby FRB" className={'offer'} src={coinsPile} />
          <img alt="Ruby FRB" className={'offer'} src={offerSVG} />
          <img alt="Ruby FRB" className={'rubyFRBHome'} src={rubyHomeSVG} />
        </div>

        {/* Bet amount section */}
        <div className={classes.betSection}>
          {/* Ruby section */}
          <div className={classes.providerSection}>
            <img alt="Ruby FRB" className={'rubyLogo'} src={rubySVG} />
          </div>
          <div className={classes.betSectionInner}>
            <img alt="Awarded Feature" className={classes.awardedFeatureIcon} src={awardedFeatureSVG} />
            <div className={classes.betAmountContainer}>
              <CoinIcon className={classes.coinIcon} />
              <span className={classes.betValue}>{formatValueWithK(Number(betAmount), t)}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div
      ref={parentRef}
      className={classNames(
        newFreeSpinsIconUiFlag && classes.frbWidgetNew,
        classes.frbWidget,
        newFreeSpinsIconUiFlag && 'frb-widget-wrapper-new',
        props.className,
      )}
      data-testid={dataTestId}
      style={{ fontSize, width, height }}
    >
      {newFreeSpinsIconUiFlag ? (
        <FreeSpinsIconNew
          betAmount={betAmount}
          betCoinType={betCoinType}
          countdownSeconds={isCountdownType(props) ? props.countdownSeconds : 0}
          gameImage={gameImage}
          spinsCount={props.rounds || 0}
          type={props.type}
        />
      ) : (
        <>
          <div className="circle"></div>
          <div className={classes.circle} style={{ border: `0.7em solid ${color}` }}></div>
          <img className={classes.gameImage} src={gameImage} />
          <div className={classes.content} style={{ background: color }}>
            {isFreeSpinsType(props) && <FreeSpinsType spinsCount={props.rounds} />}

            {isCountdownType(props) && <CountdownType countdownMinutes={props.countdownSeconds} />}

            <div className={classes.betAmount}>
              <CoinIcon className={classes.coin} />
              <span>{formatValueWithK(Number(betAmount), t)}</span>
              <span>{t('frbWidget.each')}</span>
            </div>
          </div>
          <img className={classes.giftIcon} src={giftIcon} />
        </>
      )}
    </div>
  );
}
